import { CaretDownIcon } from '@phosphor-icons/react'
import * as Tabs from '@radix-ui/react-tabs'
import { useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useMemo } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'
import { BeaconScanButton } from '../../components/beacon/beacon-scan-button.tsx'
import { Breadcrumb } from '../../components/breadcrumb.tsx'
import { ErrorBoundary } from '../../components/error-boundaries/error-boundary'
import { NewerScanBanner } from '../../components/newer-scan-banner/newer-scan-banner.tsx'

import { BranchSelector } from '../../components/menus/branch-selector'
import { RepositoryActionMenu } from '../../components/menus/repository-action-menu'
import {
  RepositoryActionMenuStoreProvider,
  useRepositoryActionMenuStore,
} from '../../components/menus/repository-action-menu.store'
import { ToolSelector } from '../../components/menus/tool-selector'
import { useGetToolsForRepository } from '../../components/menus/tool-selector.hooks'
import { RepositoryActionOverlays } from '../../components/repository-action-overlays'
import { RepositoryHeader } from '../../components/repository-header'
import { Spinner } from '../../components/spinner'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { Routes } from '../../routes.ts'
import { useToastService } from '../../services/toast-service.tsx'
import {
  invalidateGetScans,
  useAdminRole,
  useGetRepository,
  useGetRepositoryIntegrations,
  useGetScans,
  useGetWorkflows,
  usePostUploadScan,
} from '../../utils/api-client/api-hooks'
import { Repository, type Tool } from '../../utils/api-client/api-schemas.ts'
import { isAiScannerTool } from '../../utils/detector-utils.ts'
import { convertIntegrationTypeToTool, getToolName } from '../../utils/repository-utils.tsx'
import { getRuntimeEnv, isLabsEnabled } from '../../utils/runtime-env.ts'
import * as scansOverviewPageStyles from '../scans-overview-page.css'
import { BEACON_TAB_VIEW_PARAM, BeaconTab, type BeaconTabView } from './beacon-tab.tsx'
import { ExploitChainsTab } from './exploit-chains-tab.tsx'
import { FindingOverlaysTab } from './finding-overlays-tab.tsx'
import { FindingsTabWithProviders } from './findings-tab.tsx'
import * as styles from './repository-details-page.css.ts'
import {
  useBranchFilter,
  useRepositoryDetailsPageParams,
  useResolvedBranch,
  useSelectBranchOnLoad,
  useSelectedBranch,
  useSelectedTool,
  useSelectToolOnLoad,
  useShownBranch,
  useSwitchTool,
} from './repository-details-page.hooks.tsx'
import { patchSearchParams } from './scans-list.tsx'
import { ScansTab } from './scans-tab.tsx'
import { SURFACE_SECTION_PARAM, THREAT_MODEL_VIEW_PARAM, type ThreatModelView } from './threat-model-navigation.ts'
import { ThreatModelTab } from './threat-model-tab.tsx'

const RepositoryDetailsPageTabSchema = z.enum([
  'findings',
  'scans',
  'threat-model',
  'exploit-chains',
  'finding-overlays',
  'beacon',
])
export type RepositoryDetailsPageTab = z.infer<typeof RepositoryDetailsPageTabSchema>

type LegacyTabRedirect = {
  tab: RepositoryDetailsPageTab
  threatModelView?: ThreatModelView
  beaconView?: BeaconTabView
}

/**
 * Where old tab URL values now live. Bookmarks and links in Slack still carry them. The generated
 * opengrep rules became Beacon's natural-language rules, and the AI detection view they once shared a
 * tab with became Beacon. Both were Labs features, so without Labs they land on findings.
 */
const LEGACY_TAB_REDIRECTS = new Map<string, LegacyTabRedirect>([
  ['opengrep-rules', { tab: 'beacon', beaconView: 'rules' }],
  ['detections', { tab: 'beacon' }],
])

/** The threat model's Rules view (once `beacons`) moved to the Beacon tab; links to it still arrive. */
const MOVED_THREAT_MODEL_VIEWS = new Set(['rules', 'beacons'])

const legacyRedirectFor = (searchParams: URLSearchParams): LegacyTabRedirect | undefined => {
  const rawTab = searchParams.get('tab')
  const legacyTab = rawTab ? LEGACY_TAB_REDIRECTS.get(rawTab) : undefined
  if (legacyTab) {
    return legacyTab
  }
  const rawView = searchParams.get(THREAT_MODEL_VIEW_PARAM)
  return rawTab === 'threat-model' && rawView && MOVED_THREAT_MODEL_VIEWS.has(rawView)
    ? { tab: 'beacon', beaconView: 'rules' }
    : undefined
}

const parseTab = ({ raw, isLabsEnabled }: { raw: string | null; isLabsEnabled: boolean }): RepositoryDetailsPageTab => {
  const tab = RepositoryDetailsPageTabSchema.safeParse(raw).data ?? 'findings'
  return tab === 'beacon' && !isLabsEnabled ? 'findings' : tab
}

const canonicalSearch = ({
  searchParams,
  legacy,
  isLabsEnabled,
}: {
  searchParams: URLSearchParams
  legacy: LegacyTabRedirect
  isLabsEnabled: boolean
}): string =>
  patchSearchParams(searchParams, {
    tab: isLabsEnabled ? legacy.tab : null,
    [THREAT_MODEL_VIEW_PARAM]: isLabsEnabled ? (legacy.threatModelView ?? null) : null,
    [BEACON_TAB_VIEW_PARAM]: isLabsEnabled ? (legacy.beaconView ?? null) : null,
    detectionSource: null,
  }).toString()

/**
 * The tabs whose content is scoped by the selected tool.
 *
 * Findings and Scans pass `?tool=` into their queries. The threat model is built for the repository as
 * a whole, and everything derived from it — exploit chains, scan prioritization — spans every tool's
 * findings at once; all three query by `repositoryId` alone. Offering a live tool selector on those
 * three implies a filter that does not exist.
 */
const TOOL_SCOPED_TABS: readonly RepositoryDetailsPageTab[] = ['findings', 'scans']

const REPOSITORY_WIDE_REASON =
  'Threat models and the views derived from them are not bound to a single tool, but rather cover ' +
  'the entire repository across all tools.'

const BRANCH_REPOSITORY_WIDE_REASON =
  'Threat models and the views derived from them are not bound to a single branch, but rather cover ' +
  'the entire repository.'

export const RepositoryDetailsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { isLabsEnabled } = getRuntimeEnv()
  const rawTab = searchParams.get('tab')
  const legacy = legacyRedirectFor(searchParams)
  const tab = parseTab({ raw: rawTab, isLabsEnabled })

  const setTab = useCallback(
    (value: RepositoryDetailsPageTab) => {
      setSearchParams(prev =>
        patchSearchParams(prev, {
          tab: value === 'findings' ? null : value,
          // The section anchor is a deep-link into one threat-model inventory section. Leaving the
          // threat model for another top-level tab ends that arrival, so drop it — otherwise coming
          // back re-scrolls and re-highlights a section the reader has moved on from.
          [SURFACE_SECTION_PARAM]: null,
        })
      )
    },
    [setSearchParams]
  )

  useSelectToolOnLoad()
  useSelectBranchOnLoad()

  if (legacy) {
    return <Navigate replace to={{ search: canonicalSearch({ searchParams, legacy, isLabsEnabled }) }} />
  }

  return (
    <RepositoryActionMenuStoreProvider>
      <div style={{ display: 'flex', flexDirection: 'column', margin: '24px 0', gap: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', margin: '0 72px' }}>
          <RepositoryDetailsPageBreadcrumb />
          {tab === 'findings' && <RepositoryDetailsPageNewerScanBanner />}
        </div>

        <HeaderSection />

        <Tabs.Root value={tab} onValueChange={value => setTab(RepositoryDetailsPageTabSchema.parse(value))}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>
            <Tabs.List className={styles.tabList} aria-label="Repository sections">
              <div style={{ display: 'flex', width: '100%', gap: '48px' }}>
                <ToolSelectorWrapper />
                <div>
                  <Tabs.Trigger value="findings" className={scansOverviewPageStyles.tab}>
                    Findings
                  </Tabs.Trigger>
                  <Tabs.Trigger value="scans" className={scansOverviewPageStyles.tab}>
                    Scans
                  </Tabs.Trigger>
                  <Tabs.Trigger value="threat-model" className={scansOverviewPageStyles.tab}>
                    Threat model
                  </Tabs.Trigger>
                  <Tabs.Trigger value="exploit-chains" className={scansOverviewPageStyles.tab}>
                    Exploit chains
                  </Tabs.Trigger>
                  <Tabs.Trigger value="finding-overlays" className={scansOverviewPageStyles.tab}>
                    Scan prioritization
                  </Tabs.Trigger>
                  {isLabsEnabled && (
                    <Tabs.Trigger value="beacon" className={scansOverviewPageStyles.tab}>
                      Beacon
                    </Tabs.Trigger>
                  )}
                </div>
              </div>
            </Tabs.List>
            <div style={{ margin: '0 72px' }}>
              <Tabs.Content value="findings" tabIndex={-1}>
                <ErrorBoundary fallbackTitle="Unable to load findings">
                  <FindingsTabWithProviders />
                </ErrorBoundary>
              </Tabs.Content>
              <Tabs.Content value="scans" tabIndex={-1}>
                <ErrorBoundary fallbackTitle="Unable to load scans">
                  <ScansTab />
                </ErrorBoundary>
              </Tabs.Content>
              <Tabs.Content value="threat-model" tabIndex={-1}>
                <ErrorBoundary fallbackTitle="Unable to load threat model">
                  <ThreatModelTab />
                </ErrorBoundary>
              </Tabs.Content>
              <Tabs.Content value="exploit-chains" tabIndex={-1}>
                <ErrorBoundary fallbackTitle="Unable to load exploit chains">
                  <ExploitChainsTab onSwitchToThreatModel={() => setTab('threat-model')} />
                </ErrorBoundary>
              </Tabs.Content>
              <Tabs.Content value="finding-overlays" tabIndex={-1}>
                <ErrorBoundary fallbackTitle="Unable to load scan prioritization">
                  <FindingOverlaysTab onSwitchToThreatModel={() => setTab('threat-model')} />
                </ErrorBoundary>
              </Tabs.Content>
              {isLabsEnabled && (
                <Tabs.Content value="beacon" tabIndex={-1}>
                  <ErrorBoundary fallbackTitle="Unable to load Beacon">
                    <BeaconTab onSwitchToThreatModel={() => setTab('threat-model')} />
                  </ErrorBoundary>
                </Tabs.Content>
              )}
            </div>
          </div>
        </Tabs.Root>
      </div>
    </RepositoryActionMenuStoreProvider>
  )
}

const HeaderSection: React.FC = () => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { data: repository } = useGetRepository({ repositoryId })
  const { data: workflows } = useGetWorkflows({ repositoryId })
  const hasWorkflows = (workflows?._embedded?.items?.length ?? 0) > 0
  const { setCurrentOverlay } = useRepositoryActionMenuStore(
    useShallow(({ setCurrentOverlay }) => ({ setCurrentOverlay }))
  )
  const { selectedTool } = useSelectedTool()
  const shownBranch = useShownBranch()

  return (
    <section style={{ paddingTop: 0, paddingBottom: 0, margin: '0 72px' }} aria-label="Repository Header">
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'end' }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
          <RepositoryHeader
            repository={repository}
            // Without a tool, or when the tool has scanned no branch, there is nothing to pick between,
            // so the header falls back to its plain default-branch tag.
            showDefaultBranchTag={true}
            branchSlot={
              selectedTool && shownBranch ? (
                <BranchSelectorWrapper tool={selectedTool} selectedBranch={shownBranch} />
              ) : undefined
            }
            hasWorkflows={hasWorkflows}
            onWorkflowClick={() => setCurrentOverlay('manage-workflows')}
            isPageTitle={true}
          />
        </div>
        <div className={styles.headerActions}>
          {/* Secondary: it is on every tab, so a tab's own call to action stays the one primary. */}
          {isLabsEnabled() && <BeaconScanButton repositoryId={repositoryId} buttonType="secondary" size="small" />}
          <RepositoryActionMenuWithOverlays repository={repository} />
        </div>
      </div>
    </section>
  )
}

const ToolSelectorWrapper = () => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const [searchParams] = useSearchParams()
  const { isLabsEnabled } = getRuntimeEnv()
  const isToolScoped = TOOL_SCOPED_TABS.includes(parseTab({ raw: searchParams.get('tab'), isLabsEnabled }))

  const { selectedTool } = useSelectedTool()
  const switchTool = useSwitchTool()
  const { data: tools, isLoading } = useGetToolsForRepository({ repositoryId })

  if (isLoading) {
    return <Spinner />
  }

  // Pixee's built-in scanners are offered to every repository but never chosen for one on load (see
  // `pickInitialTool`), so a repository with no tool of its own reads as having none.
  if (tools.size === 0 || (!selectedTool && [...tools].every(isAiScannerTool))) {
    return (
      <div className={styles.noRelevantTools}>
        <span className={styles.noRelevantToolsLabel}>No relevant tools</span>
        <CaretDownIcon className={styles.noRelevantToolsCaret} size={24} weight="fill" />
      </div>
    )
  }

  if (!selectedTool) {
    return null
  }

  return (
    <ToolSelector
      repositoryId={repositoryId}
      selectedTool={selectedTool}
      setSelectedTool={switchTool}
      tools={Array.from(tools)}
      isDisabled={!isToolScoped}
      disabledReason={REPOSITORY_WIDE_REASON}
    />
  )
}

const BranchSelectorWrapper: React.FC<{
  tool: Tool
  selectedBranch: string
}> = ({ tool, selectedBranch }) => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { data: repository } = useGetRepository({ repositoryId })
  const [searchParams] = useSearchParams()
  const { isLabsEnabled } = getRuntimeEnv()
  const isToolScoped = TOOL_SCOPED_TABS.includes(parseTab({ raw: searchParams.get('tab'), isLabsEnabled }))
  const { urlBranch, selectBranch } = useSelectedBranch()

  return (
    <BranchSelector
      // Keyed so recently visited branches and the open state never carry over between repositories.
      key={repositoryId}
      repositoryId={repositoryId}
      branchesHref={repository?._links.branches?.href}
      tool={tool}
      urlBranch={urlBranch}
      selectedBranch={selectedBranch}
      onSelectBranch={selectBranch}
      // The branch scopes exactly what the tool does, so it goes inert on the same tabs.
      isDisabled={!isToolScoped}
      disabledReason={BRANCH_REPOSITORY_WIDE_REASON}
    />
  )
}

const RepositoryActionMenuWithOverlays = ({ repository }: { repository?: Repository }) => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const repositoryActionProps = useRepositoryActionMenuStore(
    useShallow(({ currentOverlay, setCurrentOverlay }) => ({ currentOverlay, setCurrentOverlay }))
  )
  const { addToast } = useToastService()
  const { hasWriteAccess } = useAdminRole()
  const { data: integrations } = useGetRepositoryIntegrations({ repositoryId })
  const mappedTools: Tool[] = useMemo(
    () => [
      ...new Set(
        (integrations?._embedded?.items ?? []).map(integration => convertIntegrationTypeToTool(integration.type))
      ),
    ],
    [integrations]
  )
  const showSyncError = useShowMutationError<typeof usePostUploadScan>({
    prefix: (_error, variables) =>
      `Failed to sync ${getToolName(variables.body.metadata.tool)} findings to ${repository?.name ?? 'repository'}`,
  })
  const syncToolMutation = usePostUploadScan()
  const handleSyncTool = useCallback(
    (tool: Tool) => {
      if (!repository) return
      syncToolMutation.mutate(
        { repositoryId: repository.id, body: { metadata: { tool, workflow_execution_policy: 'execute' } } },
        {
          onSuccess: () => {
            addToast({
              message: (
                <>
                  Latest {getToolName(tool)} findings synced to {repository.name}
                </>
              ),
              variant: 'success',
            })
          },
          onError: showSyncError,
        }
      )
    },
    [syncToolMutation, addToast, repository, showSyncError]
  )

  return (
    <>
      <RepositoryActionMenu
        {...repositoryActionProps}
        mappedTools={mappedTools}
        onSyncTool={handleSyncTool}
        hasWriteAccess={hasWriteAccess}
      />
      {repository && <RepositoryActionOverlays repository={repository} {...repositoryActionProps} />}
    </>
  )
}

const RepositoryDetailsPageNewerScanBanner: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { selectedTool } = useSelectedTool()
  const branchFilter = useBranchFilter()
  const toolsFilter = selectedTool ? [selectedTool] : undefined

  const { data: analyzedScans } = useGetScans({
    page: { number: 0, size: 1 },
    repositoryIds: [repositoryId],
    tools: toolsFilter,
    analysisState: 'completed',
    ...branchFilter,
  })
  const { data: allScans } = useGetScans({
    page: { number: 0, size: 1 },
    repositoryIds: [repositoryId],
    tools: toolsFilter,
    ...branchFilter,
  })

  const currentScanId = analyzedScans?._embedded?.items?.[0]?.id ?? allScans?._embedded?.items?.[0]?.id

  if (!currentScanId || !selectedTool || !branchFilter.enabled) return null

  return (
    <div style={{ marginLeft: 'auto' }}>
      <NewerScanBanner
        currentScanId={currentScanId}
        repositoryId={repositoryId}
        tool={selectedTool}
        branch={branchFilter.branch}
        onView={({ newerScanId, state }) => {
          if (state === 'has-analysis') {
            invalidateGetScans(queryClient)
          } else {
            navigate(Routes.ScanDetailsPage.createPath({ scanId: newerScanId }))
          }
        }}
        viewLabel="View"
      />
    </div>
  )
}

const RepositoryDetailsPageBreadcrumb: React.FC = () => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { data: repository } = useGetRepository({ repositoryId })
  const [searchParams] = useSearchParams()
  const { isLabsEnabled } = getRuntimeEnv()
  const isToolScoped = TOOL_SCOPED_TABS.includes(parseTab({ raw: searchParams.get('tab'), isLabsEnabled }))
  const { selectedTool } = useSelectedTool()
  const { branch } = useResolvedBranch()

  return (
    <RepositoryDetailsPageBreadcrumbPure
      repositoryName={repository?.name}
      branch={(isToolScoped ? branch : undefined) ?? repository?.default_branch}
      // The suffix names what the page below is scoped to. On the repository-wide views it would
      // assert a tool scope that does not exist — the same claim the inert selector stopped making.
      toolName={isToolScoped ? getToolName(selectedTool ?? undefined) : undefined}
    />
  )
}

export type RepositoryDetailsPageBreadcrumbPureProps = {
  repositoryName?: string
  branch?: string
  toolName?: string
}

export const RepositoryDetailsPageBreadcrumbPure: React.FC<RepositoryDetailsPageBreadcrumbPureProps> = ({
  repositoryName,
  branch,
  toolName,
}) => {
  // Built up piece by piece rather than all-or-nothing: dropping the tool suffix on the repository-wide
  // views must not take the branch down with it, which an `&&` chain over all three would.
  const repoLabel = repositoryName
    ? `${repositoryName}${branch ? ` (${branch})` : ''}${toolName ? `(${toolName})` : ''}`
    : repositoryName

  return (
    <Breadcrumb
      items={[
        { label: 'All repositories', to: Routes.RepositoriesPage.path },
        { label: repoLabel ?? 'Repository details', to: '#' },
      ]}
    />
  )
}
